// ===========================================
// Admin Hybrid Pre-Booking Page
// ===========================================
// Allows admins to reserve hybrid individual slots for specific families
// before the booking window opens to everyone.

import { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Lock as LockIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  History as HistoryIcon,
  SchoolOutlined as TeacherIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useHybridBookingStatus, useAdminAvailableSlots, useAdminPreBookings, useCreateAdminPreBooking, useCancelAdminPreBooking, useLessonBookings, useEligibleClinicTeachers } from '../../hooks/useHybridBooking';
import { useLessons } from '../../hooks/useLessons';
import { useTerms } from '../../hooks/useAdmin';
import { useStudents } from '../../hooks/useUsers';
import { TimeSlot } from '../../api/hybridBooking.api';
import { Student } from '../../api/users.api';
import { LessonEnrollment } from '../../api/lessons.api';
import PageHeader from '../../components/common/PageHeader';
import WeekTilePicker from '../../components/booking/WeekTilePicker';
import { startsAfterWeek } from '../../utils/occurrenceRoster';
import { parseUTCDate } from '../../utils/dateUtils';
import { slotDayIsPastForSchool } from '../../utils/slotFilters';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { getRunningTerm, getDefaultTermId } from '../../utils/bookableTerms';
import { getPreBookableTerms } from '../../utils/termLock';
import { useTermLock } from '../../hooks/useTermLock';

// ===========================================
// HELPERS
// ===========================================

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatSlotDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseUTCDate(date) : date;
  return format(d, 'EEE d MMM');
}

// ===========================================
// SLOT PICKER COMPONENT
// ===========================================

interface SlotPickerProps {
  slots: TimeSlot[];
  selected: TimeSlot | null;
  onSelect: (slot: TimeSlot) => void;
  isLoading: boolean;
}

function SlotPicker({ slots, selected, onSelect, isLoading }: SlotPickerProps) {
  if (isLoading) {
    return (
      <Grid container spacing={1}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={3} key={i}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (slots.length === 0) {
    return <Alert severity="info">No slots configured for this week.</Alert>;
  }

  // Group by dateKey (UTC-pure). slot.date is local-tz built on the backend
  // and can shift a day in non-UTC server timezones when re-serialised.
  const byDate = new Map<string, TimeSlot[]>();
  for (const slot of slots) {
    if (!byDate.has(slot.dateKey)) byDate.set(slot.dateKey, []);
    byDate.get(slot.dateKey)!.push(slot);
  }

  return (
    <Box>
      {[...byDate.entries()].map(([dateStr, daySlots]) => (
        <Box key={dateStr} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            {format(parseUTCDate(dateStr), 'EEEE, d MMMM')}
          </Typography>
          <Grid container spacing={1}>
            {daySlots.map((slot) => {
              const isSelected =
                selected?.dateKey === dateStr &&
                selected?.startTime === slot.startTime;
              const isTaken = !slot.isAvailable;
              return (
                <Grid item xs={3} sm={2} key={`${dateStr}-${slot.startTime}`}>
                  <Paper
                    variant="outlined"
                    onClick={() => {
                      if (!isTaken) onSelect(slot);
                    }}
                    sx={{
                      py: 0.75,
                      px: 0.5,
                      textAlign: 'center',
                      cursor: isTaken ? 'not-allowed' : 'pointer',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected
                        ? 'primary.main'
                        : isTaken
                          ? 'action.disabledBackground'
                          : 'background.paper',
                      color: isSelected
                        ? 'primary.contrastText'
                        : isTaken
                          ? 'text.disabled'
                          : 'text.primary',
                      opacity: isTaken ? 0.5 : 1,
                      transition: 'all 0.15s',
                      '&:hover': isTaken
                        ? {}
                        : {
                            borderColor: 'primary.main',
                            bgcolor: isSelected ? 'primary.dark' : 'primary.50',
                          },
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                      {slot.startTime}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

// ===========================================
// MAIN PAGE
// ===========================================

export default function AdminHybridPreBookingPage() {
  // --- Selection State ---
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  // selectedWeek = the 1:1 CLINIC week the booking is FOR (step 2, individual
  // weeks only). Gates which students are eligible.
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  // scheduledWeek = the term week the slot is actually SCHEDULED into (step 3,
  // any week). Defaults to selectedWeek but the admin can move it, exactly like
  // the 1:1 clinic reschedule. The saved booking's weekNumber follows this.
  const [scheduledWeek, setScheduledWeek] = useState<number | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  // overrideTeacherId = an admin-chosen teacher for THIS clinic (Section 3).
  // null = use the student's regular (lesson) teacher. Changing it re-fetches
  // the slot grid against the new teacher's availability.
  const [overrideTeacherId, setOverrideTeacherId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);

  // --- Data ---
  const { data: bookingStatus, isLoading: statusLoading } = useHybridBookingStatus();
  const { data: allLessons, isLoading: lessonsLoading } = useLessons();
  const { data: terms } = useTerms();

  // Terms an admin can pre-book: active and not LOCKED. A term that has only finished stays
  // offered ("(Ended)") until the NEXT term starts; a not-yet-started term (school holidays) is
  // selectable too, since pre-booking is specifically for setting a term up ahead of time.
  const schoolTimeZone = useSchoolTimezone();
  const { isLocked, schoolToday } = useTermLock(terms);
  const bookableTerms = getPreBookableTerms(terms, (t) => isLocked(t.id));
  const runningTerm = getRunningTerm(bookableTerms, new Date(), schoolTimeZone);
  const defaultTermId = getDefaultTermId(bookableTerms, new Date(), schoolTimeZone);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const effectiveTermId = selectedTermId ?? defaultTermId;
  // The full term being booked — drives the week picker's start/end. Must be the
  // SELECTED term (which may be a future one), not just whatever is running now.
  const effectiveTerm = bookableTerms.find((t) => t.id === effectiveTermId) ?? null;

  const lessons = allLessons?.filter(
    (l) => l.HybridLessonPattern != null && l.termId === effectiveTermId
  );
  const { data: allStudents } = useStudents();

  const selectedLesson = lessons?.find((l) => l.id === selectedLessonId);

  const individualWeeks = (selectedLesson?.HybridLessonPattern?.individualWeeks ?? []) as number[];

  // Slot grid follows the SCHEDULED week (step 3), so the admin can move the
  // booking into any term week's availability — mirrors the reschedule modal.
  // When a teacher override (Section 3) is set, slots reflect THAT teacher's
  // availability; the query key includes overrideTeacherId so a change re-fetches.
  const { data: weekSlots, isLoading: slotsLoading } = useAdminAvailableSlots(
    selectedLessonId ?? '',
    scheduledWeek ?? 0,
    undefined,
    overrideTeacherId ?? undefined
  );
  // ⚠️ PAIRED WITH `allowPast` ON THE SCHEDULED-WEEK PICKER — they must move
  // together. Admin pre-booking is the ONE path documented as existing for
  // back-dating ("a back-dated pre-booking is the documented PURPOSE of this
  // path" — hybridBooking.service.ts, createAdminPreBooking), so MNM can record
  // a 1:1 arranged off-platform and never entered. The server honours it:
  // `getAdminAvailableSlots` has NO past filter (the one in
  // `generateOpenClinicSlots` serves the PARENT flow and is untouched), the
  // family confirmation is suppressed for a past date, and a CLINIC_PRE_BOOKED
  // audit row is written. Dropping elapsed days here would leave a selectable
  // week with zero slots — a dead end that reads as a loading bug.
  // (MNM request 4, 2026-09-19.)
  const bookableSlots = weekSlots ?? [];

  // Is the CHOSEN slot in the past? Keyed on the slot's own date, not the week:
  // a part-past week offers both elapsed and remaining days, so the week alone
  // would warn on an ordinary forward booking.
  //
  // WARNING: uses the SCHOOL's day, not `startOfTodayUTC()` - see
  // `slotDayIsPastForSchool`. The backend decides the same question with
  // `schoolTodayUtcMidnight`, and the two disagree for ~10 hours every Sydney
  // morning; the first cut of this notice was silent in exactly that window.
  const selectedSlotIsPast = useMemo(
    () => slotDayIsPastForSchool(selectedSlot, schoolTimeZone),
    [selectedSlot, schoolTimeZone]
  );
  // Teachers assignable to this clinic (Section 3): all active, instrument-
  // qualified teachers at the school, with the lesson's primary flagged default.
  const { data: eligibleTeachers } = useEligibleClinicTeachers(selectedLessonId ?? '');
  // All active bookings for this lesson (any week). Resolved by CLINIC week
  // (clinicWeekNumber ?? weekNumber) client-side, this drives both the step-2
  // reserved-count chips and the per-student greying — so a student booked FOR
  // a clinic week is matched even when scheduled into a different week.
  const { data: lessonBookings } = useLessonBookings(selectedLessonId ?? '', {});
  // Pre-bookings list for the bottom table. Scoped to the selected CLINIC week
  // (clinicWeekNumber) so the table is consistent with the clinic-week reserved
  // counts on the chips — a clinic scheduled into another week still appears
  // under its clinic week here.
  const { data: preBookings, isLoading: preBookingsLoading } = useAdminPreBookings(
    selectedLessonId
      ? { lessonId: selectedLessonId, ...(selectedWeek ? { clinicWeekNumber: selectedWeek } : {}) }
      : undefined
  );

  const createPreBooking = useCreateAdminPreBooking();
  const cancelPreBooking = useCancelAdminPreBooking();

  // --- Derived data ---
  // Active bookings for the selected CLINIC week (clinicWeekNumber ?? weekNumber).
  const clinicWeekBookings = (lessonBookings ?? []).filter(
    (b) => b.status !== 'CANCELLED' && (b.clinicWeekNumber ?? b.weekNumber) === selectedWeek
  );
  const enrolledStudentIds = new Set(
    clinicWeekBookings.map((b) => b.Student?.id).filter(Boolean)
  );
  // Subset that are admin pre-bookings — for the distinct "Pre-reserved" chip.
  const preBookedStudentIds = new Set(
    clinicWeekBookings
      .filter((b) => b.isAdminPreBooking)
      .map((b) => b.Student?.id)
      .filter(Boolean)
  );

  const enrolledStudents: Student[] = allStudents?.filter((s: Student) =>
    selectedLesson?.LessonEnrollment?.some((e: LessonEnrollment) => e.studentId === s.id && e.isActive)
  ) ?? [];

  // --- Handlers ---
  const handleLessonSelect = (lessonId: string) => {
    setSelectedLessonId(lessonId);
    setSelectedWeek(null);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setOverrideTeacherId(null);
    setSelectedSlot(null);
  };

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
    // The selected class belongs to the old term — clear the whole selection.
    setSelectedLessonId(null);
    setSelectedWeek(null);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setOverrideTeacherId(null);
    setSelectedSlot(null);
  };

  const handleWeekSelect = (week: number) => {
    setSelectedWeek(week);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setOverrideTeacherId(null);
    setSelectedSlot(null);
  };

  const handleStudentSelect = (studentId: string, familyId: string) => {
    setSelectedStudentId(studentId);
    setSelectedFamilyId(familyId);
    // Default the scheduled week to the clinic week; admin can move it in step 3.
    setScheduledWeek(selectedWeek);
    // Each new student starts on their regular teacher (Section 3 default).
    setOverrideTeacherId(null);
    setSelectedSlot(null);
  };

  // Section 3: admin overrides the clinic teacher. Empty string = default
  // (student's regular teacher → null). Clear the slot so it re-picks against
  // the new teacher's availability (the slot query re-fetches via its key).
  const handleTeacherSelect = (teacherId: string) => {
    setOverrideTeacherId(teacherId || null);
    setSelectedSlot(null);
  };

  // Step 3: admin picks which term week the slot is actually scheduled into.
  const handleScheduledWeekSelect = (week: number) => {
    setScheduledWeek(week);
    setSelectedSlot(null);
  };

  const handlePreBook = async () => {
    if (!selectedLessonId || !selectedWeek || !scheduledWeek || !selectedStudentId || !selectedFamilyId || !selectedSlot) return;

    const dateKey =
      selectedSlot.dateKey ??
      (selectedSlot.date ? new Date(selectedSlot.date).toISOString().split('T')[0] : '');
    createPreBooking.mutate({
      lessonId: selectedLessonId,
      studentId: selectedStudentId,
      familyId: selectedFamilyId,
      // weekNumber follows the SCHEDULED week (the actual date), matching the
      // 1:1 clinic reschedule write-path.
      weekNumber: scheduledWeek,
      // clinicWeekNumber = the 1:1 clinic week the booking is FOR (step 2).
      clinicWeekNumber: selectedWeek,
      scheduledDate: `${dateKey}T00:00:00.000Z`,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      adminNotes: adminNotes.trim() || undefined,
      // Only sent when the admin overrode the regular teacher (Section 3).
      overrideTeacherId: overrideTeacherId ?? undefined,
    }, {
      onSuccess: () => {
        setSelectedSlot(null);
        setSelectedStudentId(null);
        setSelectedFamilyId(null);
        setScheduledWeek(null);
        setOverrideTeacherId(null);
        setAdminNotes('');
      },
    });
  };

  const handleCancelConfirm = () => {
    if (!cancelTarget) return;
    cancelPreBooking.mutate(cancelTarget, {
      onSuccess: () => setCancelTarget(null),
    });
  };

  const hasHybridLessons = (bookingStatus?.totalHybridLessons ?? 0) > 0;

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Box>
      <PageHeader
        title="Pre-Book Slots"
        subtitle="Reserve hybrid individual session slots for specific families before bookings open"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Pre-Book Slots' },
        ]}
      />

      {!hasHybridLessons && !statusLoading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No hybrid lessons found. Create a hybrid lesson first.
        </Alert>
      )}
      {hasHybridLessons && bookableTerms.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No current or upcoming terms. Set up a term (with future dates) to pre-book its clinics. Ended terms are closed to edits on purpose — records for a term must be tidied up before the next one starts.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ===================== */}
        {/* PANEL 1: Lesson Selector */}
        {/* ===================== */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon color="primary" fontSize="small" />
                  <Typography variant="h6">1. Select Students Class</Typography>
                </Box>
              }
            />
            <Divider />
            <CardContent sx={{ p: 0 }}>
              {bookableTerms.length > 0 && (
                <Box sx={{ p: 2, pb: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="prebook-term-label">Term</InputLabel>
                    <Select
                      labelId="prebook-term-label"
                      label="Term"
                      value={effectiveTermId ?? ''}
                      onChange={(e) => handleTermChange(e.target.value)}
                    >
                      {bookableTerms.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.name}
                          {runningTerm?.id === t.id
                            ? ' (current)'
                            : new Date(t.endDate) < schoolToday
                              ? ' (Ended)'
                              : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {lessonsLoading || statusLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3].map((i) => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)}
                </Box>
              ) : lessons?.filter((l) => l.HybridLessonPattern).length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  No hybrid lessons in this term.
                </Typography>
              ) : (
                lessons?.filter((l) => l.HybridLessonPattern).map((lesson) => {
                  const isSelected = selectedLessonId === lesson.id;
                  const individualWks = (lesson.HybridLessonPattern?.individualWeeks ?? []) as number[];
                  return (
                    <Box
                      key={lesson.id}
                      onClick={() => handleLessonSelect(lesson.id)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {lesson.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lesson.dayOfWeek != null ? DAY_NAMES[lesson.dayOfWeek] : 'TBD'} • {lesson.startTime ?? 'TBD'}–{lesson.endTime ?? 'TBD'}
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(lesson.Teacher ?? lesson.Instructor) && (
                          <Chip
                            label={`${(lesson.Teacher ?? lesson.Instructor)!.User.firstName} ${(lesson.Teacher ?? lesson.Instructor)!.User.lastName}`}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 18,
                              ...((lesson.Teacher ?? lesson.Instructor)!.color
                                ? {
                                    backgroundColor: (lesson.Teacher ?? lesson.Instructor)!.color!,
                                    color: 'white',
                                  }
                                : {}),
                            }}
                          />
                        )}
                        <Chip
                          label={lesson.HybridLessonPattern?.bookingsOpen ? 'Open' : 'Closed'}
                          size="small"
                          color={lesson.HybridLessonPattern?.bookingsOpen ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                        <Chip
                          label={`${individualWks.length} individual wk${individualWks.length !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ===================== */}
        {/* PANELS 2 & 3: Week + Student + Slot */}
        {/* ===================== */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Panel 2: Week + Student */}
            <Card>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" fontSize="small" />
                    <Typography variant="h6">2. Select Week & Student</Typography>
                  </Box>
                }
              />
              <Divider />
              <CardContent>
                {!selectedLessonId ? (
                  <Typography color="text.secondary" variant="body2">
                    Select a lesson first.
                  </Typography>
                ) : (
                  <>
                    {/* Step 2 picks the 1:1 CLINIC week the booking is FOR
                        (individual weeks only) — this also gates which students
                        are eligible. The actual scheduled date/week is chosen in
                        step 3, where the admin may move it into any term week. */}
                    <Typography variant="subtitle2" gutterBottom>Individual Weeks</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {individualWeeks.map((week) => {
                        // Count active pre-bookings FOR this clinic week
                        // (clinicWeekNumber ?? weekNumber), regardless of which
                        // week they're scheduled into.
                        const weekPreBookings = (lessonBookings ?? []).filter(
                          (b) =>
                            b.isAdminPreBooking &&
                            b.status !== 'CANCELLED' &&
                            (b.clinicWeekNumber ?? b.weekNumber) === week
                        );
                        return (
                          <Chip
                            key={week}
                            label={`Week ${week}${weekPreBookings.length > 0 ? ` (${weekPreBookings.length} reserved)` : ''}`}
                            onClick={() => handleWeekSelect(week)}
                            color={selectedWeek === week ? 'primary' : 'default'}
                            variant={selectedWeek === week ? 'filled' : 'outlined'}
                            icon={weekPreBookings.length > 0 ? <LockIcon /> : undefined}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>

                    {/* Student list */}
                    {selectedWeek && (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          Enrolled Students — Week {selectedWeek}
                        </Typography>
                        {enrolledStudents.length === 0 ? (
                          <Typography color="text.secondary" variant="body2">No enrolled students.</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {enrolledStudents.map((student) => {
                              const hasBooking = enrolledStudentIds.has(student.id);
                              const hasPreBooking = preBookedStudentIds.has(student.id);
                              const isSelected = selectedStudentId === student.id;
                              // A child who joins the class AFTER this clinic week can't be
                              // booked into it (the server 400s too) — show when they start.
                              const startsInWeek = startsAfterWeek(
                                selectedLesson,
                                selectedLesson?.LessonEnrollment,
                                student.id,
                                selectedWeek
                              );
                              const isSelectable = !hasBooking && !hasPreBooking && startsInWeek == null;

                              return (
                                <Box
                                  key={student.id}
                                  onClick={() => {
                                    if (isSelectable) {
                                      handleStudentSelect(student.id, student.familyId ?? '');
                                    }
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                    bgcolor: isSelected
                                      ? 'primary.50'
                                      : hasBooking || hasPreBooking
                                      ? 'action.disabledBackground'
                                      : 'background.paper',
                                    cursor: isSelectable ? 'pointer' : 'default',
                                    opacity: hasBooking || hasPreBooking ? 0.7 : 1,
                                    '&:hover': isSelectable ? { borderColor: 'primary.main' } : {},
                                  }}
                                >
                                  <Typography variant="body2" sx={{ flex: 1 }}>
                                    {student.firstName} {student.lastName}
                                  </Typography>
                                  {hasPreBooking && (
                                    <Chip label="Pre-reserved" size="small" icon={<LockIcon />} color="info" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  )}
                                  {hasBooking && !hasPreBooking && (
                                    <Chip label="Booked" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  )}
                                  {startsInWeek != null && (
                                    <Chip label={`Starts Week ${startsInWeek}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Panel 3: Teacher — admin may assign this clinic to a teacher
                other than the student's regular (lesson) teacher. Default keeps
                the regular teacher; changing it re-fetches the slot grid (step 3)
                against the chosen teacher's availability. */}
            {selectedStudentId && selectedWeek && selectedLessonId && (
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TeacherIcon color="primary" fontSize="small" />
                      <Typography variant="h6">3. Teacher</Typography>
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  <FormControl fullWidth size="small">
                    <InputLabel id="override-teacher-label" shrink>Teacher</InputLabel>
                    <Select
                      labelId="override-teacher-label"
                      label="Teacher"
                      notched
                      value={overrideTeacherId ?? ''}
                      onChange={(e) => handleTeacherSelect(e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        // Empty value = no override = the student's regular teacher.
                        // Show their name (rather than the "Teacher" placeholder) so
                        // it's clear who the clinic defaults to.
                        if (!selected) {
                          const regular = (eligibleTeachers ?? []).find((t) => t.isDefault);
                          return regular ? `${regular.name} (regular teacher)` : 'Teacher';
                        }
                        return (eligibleTeachers ?? []).find((t) => t.id === selected)?.name ?? '';
                      }}
                    >
                      {(eligibleTeachers ?? []).map((t) => (
                        <MenuItem key={t.id} value={t.isDefault ? '' : t.id}>
                          {t.isDefault ? `${t.name} (regular teacher)` : t.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Defaults to the student&apos;s regular teacher. Choosing a different
                    teacher refreshes the available times below.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Panel 4: Week + Slot Picker — admin chooses which term week the
                slot is actually scheduled into (default = clinic week) and the
                time slot, mirroring the 1:1 clinic reschedule modal. */}
            {selectedStudentId && selectedWeek && selectedLessonId && (
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon color="primary" fontSize="small" />
                      <Typography variant="h6">4. Select Date & Time Slot</Typography>
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Booking for the <strong>Week {selectedWeek}</strong> 1:1 clinic.
                    Choose any week below to schedule it — defaults to the clinic week.
                  </Alert>
                  <Typography variant="subtitle2" gutterBottom>Scheduled Week</Typography>
                  {effectiveTerm ? (
                    <WeekTilePicker
                      termStartDate={effectiveTerm.startDate}
                      termEndDate={effectiveTerm.endDate}
                      selectedWeek={scheduledWeek ?? undefined}
                      onWeekSelect={handleScheduledWeekSelect}
                      currentWeek={selectedWeek ?? undefined}
                      occurrenceDayOfWeek={selectedLesson?.dayOfWeek ?? undefined}
                      // DESTINATION week ("Choose any week below to schedule
                      // it") — the admin picks any day the teacher works, which
                      // may be an override teacher whose days differ from the
                      // lesson's entirely. Judging it by the lesson's weekday
                      // locked weeks that were still bookable.
                      pastRule="week-end"
                      // Paired with `bookableSlots` above - see the note there.
                      // Unlocking one without the other yields either a locked
                      // week or an empty one.
                      allowPast
                    />
                  ) : (
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                      No active term found.
                    </Typography>
                  )}
                  <Typography variant="subtitle2" gutterBottom>Time Slot</Typography>
                  <SlotPicker
                    slots={bookableSlots}
                    selected={selectedSlot}
                    onSelect={setSelectedSlot}
                    isLoading={slotsLoading}
                  />

                  {/* The audit consequence, stated where the choice is made. */}
                  {selectedSlotIsPast && (
                    <Alert severity="warning" icon={<HistoryIcon />} sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        This session has already taken place. Booking it records what happened — the
                        family is <strong>not</strong> sent a confirmation, and it will appear in the{' '}
                        <strong>retroactive edit report</strong> against your name.
                      </Typography>
                    </Alert>
                  )}

                  {selectedSlot && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Admin Notes (optional)"
                        multiline
                        rows={2}
                        fullWidth
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Selected: <strong>{formatSlotDate(selectedSlot.dateKey)}</strong> at <strong>{selectedSlot.startTime}–{selectedSlot.endTime}</strong>
                      </Alert>
                      <Button
                        variant="contained"
                        startIcon={createPreBooking.isPending ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                        onClick={handlePreBook}
                        disabled={createPreBooking.isPending}
                      >
                        Confirm Pre-Booking
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* ===================== */}
        {/* PRE-BOOKINGS TABLE */}
        {/* ===================== */}
        {selectedLessonId && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon color="primary" fontSize="small" />
                    <Typography variant="h6">
                      Pre-Bookings
                      {selectedLesson ? ` — ${selectedLesson.name}` : ''}
                      {selectedWeek ? ` (Week ${selectedWeek})` : ''}
                    </Typography>
                  </Box>
                }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {preBookingsLoading ? (
                  <Box sx={{ p: 2 }}>
                    {[1, 2, 3].map((i) => <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />)}
                  </Box>
                ) : !preBookings || preBookings.length === 0 ? (
                  <Typography color="text.secondary" sx={{ p: 2 }}>
                    No pre-bookings yet for this lesson.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Clinic Week</TableCell>
                          <TableCell>Scheduled</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Notes</TableCell>
                          <TableCell>Created By</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preBookings.map((booking) => (
                          <TableRow
                            key={booking.id}
                            sx={{
                              border: '1px dashed',
                              borderColor: 'primary.light',
                              '& td': { borderBottom: '1px solid', borderColor: 'divider' },
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LockIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                <Typography variant="body2">
                                  {booking.Student?.firstName}{' '}
                                  {booking.Student?.lastName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={`Week ${booking.clinicWeekNumber ?? booking.weekNumber}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {format(parseUTCDate(booking.scheduledDate), 'EEE d MMM')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {booking.startTime}–{booking.endTime}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {booking.adminNotes ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {booking.AdminCreatedBy
                                  ? `${booking.AdminCreatedBy!.firstName} ${booking.AdminCreatedBy!.lastName}`
                                  : 'Admin'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={() => setCancelTarget(booking.id)}
                                sx={{ fontSize: '0.7rem' }}
                              >
                                Cancel
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Pre-Booking?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will cancel the admin-reserved slot and make it available to all families. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Keep</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelConfirm}
            disabled={cancelPreBooking.isPending}
          >
            {cancelPreBooking.isPending ? <CircularProgress size={16} color="inherit" /> : 'Cancel Pre-Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
